import { Maximize2, Minus, Send, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent, type JSX } from "react";

import type { MailAccountDTO, MailMessageDTO, OutgoingMailRequest } from "../../../common/api/types.js";
import { PrimaryButton } from "../../../common/components/PrimaryButton.js";
import { useModalFocus } from "../../../common/components/useModalFocus.js";
import { useDeleteMailMessage, useMailMessage, useSaveDraft, useSendMail } from "../query/mailQueries.js";

/** Wie lange nach der letzten Eingabe der Entwurf auf dem Server gespeichert wird. */
const AUTOSAVE_MS = 2000;

type Fields = { to: string; cc: string; subject: string; body: string };

/**
 * Schreiben, Antworten, Entwurf fortsetzen. „Von" wählt das Postfach: das eigene oder
 * ein geteiltes wie support@ — dann geht die Nachricht unter dieser Adresse hinaus und
 * liegt für alle Mitglieder in dessen „Gesendet".
 */
export function ComposeDialog({ accounts, accountId, draftId, replyTo, onClose, onSent }: {
  accounts: MailAccountDTO[];
  accountId: string;
  draftId: string | null;
  replyTo: string | null;
  onClose: () => void;
  onSent: (messageId: string) => void;
}): JSX.Element {
  const draft = useMailMessage(draftId ? accountId : null, draftId);
  const original = useMailMessage(replyTo ? accountId : null, replyTo);
  const saveDraft = useSaveDraft();
  const send = useSendMail();
  const discardDraft = useDeleteMailMessage();

  const [from, setFrom] = useState(accountId);
  const [fields, setFields] = useState<Fields>({ to: "", cc: "", subject: "", body: "" });
  const [showCc, setShowCc] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  // Wo der gespeicherte Entwurf liegt. Wechselt „Von", zieht er beim nächsten Speichern um.
  const stored = useRef<{ accountId: string; id: string } | null>(draftId ? { accountId, id: draftId } : null);
  const dirty = useRef(false);
  const prefilled = useRef(false);

  useEffect(() => {
    if (prefilled.current) return;
    if (draftId && draft.data) {
      prefilled.current = true;
      setFields({ to: draft.data.to.map(format).join(", "), cc: draft.data.cc.map(format).join(", "), subject: draft.data.subject, body: draft.data.text });
      setShowCc(draft.data.cc.length > 0);
    } else if (replyTo && original.data) {
      prefilled.current = true;
      setFields({ to: original.data.from ? format(original.data.from) : "", cc: "", subject: replySubject(original.data.subject), body: quote(original.data) });
    }
  }, [draft.data, draftId, original.data, replyTo]);

  const update = (patch: Partial<Fields>) => { dirty.current = true; setFields((current) => ({ ...current, ...patch })); };
  const hasContent = Object.values(fields).some((value) => value.trim());
  const body = (): OutgoingMailRequest => ({
    to: [fields.to],
    cc: fields.cc ? [fields.cc] : [],
    subject: fields.subject,
    text: fields.body,
    ...(replyTo ? { inReplyToId: replyTo } : {}),
  });

  const persist = async (): Promise<void> => {
    if (!dirty.current || !hasContent) return;
    dirty.current = false;
    const previous = stored.current;
    const sameAccount = previous?.accountId === from;
    const result = await saveDraft.mutateAsync({ accountId: from, body: { ...body(), ...(sameAccount && previous ? { draftId: previous.id } : {}) } });
    if (previous && !sameAccount) discardDraft.mutate({ accountId: previous.accountId, messageId: previous.id });
    stored.current = { accountId: from, id: result.id };
    setSavedAt(new Date());
  };

  useEffect(() => {
    if (!dirty.current) return;
    const timer = window.setTimeout(() => { void persist().catch(() => { dirty.current = true; }); }, AUTOSAVE_MS);
    return () => window.clearTimeout(timer);
    // persist liest den aktuellen Stand selbst; neu geplant wird nur bei Eingaben.
  }, [fields, from]);

  const close = (): void => { void persist().catch(() => undefined); onClose(); };
  const panel = useModalFocus<HTMLFormElement>(close);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    const previous = stored.current;
    send.mutate(
      { accountId: from, body: { ...body(), ...(previous?.accountId === from ? { draftId: previous.id } : {}) } },
      {
        onSuccess: (result) => {
          if (previous && previous.accountId !== from) discardDraft.mutate({ accountId: previous.accountId, messageId: previous.id });
          onSent(result.id);
        },
      },
    );
  };
  const discard = (): void => {
    const previous = stored.current;
    if (previous) discardDraft.mutate({ accountId: previous.accountId, messageId: previous.id });
    onClose();
  };

  const title = replyTo ? "Antworten" : draftId ? "Entwurf" : "Neue Nachricht";
  const status = send.error ? send.error.message : saveDraft.isPending ? "Wird gespeichert …" : savedAt ? `Entwurf gespeichert ${savedAt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}` : "Entwurf wird automatisch gespeichert";

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 p-0 sm:items-end sm:justify-end sm:p-5">
      <button type="button" aria-label="E-Mail schließen" onClick={close} className="absolute inset-0" />
      <form ref={panel} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} onSubmit={submit} className={["relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl bg-surface-raised shadow-2xl sm:rounded-2xl", expanded ? "sm:h-[calc(100vh-2.5rem)] sm:max-w-4xl" : "sm:h-[38rem] sm:max-w-2xl"].join(" ")}>
        <header className="flex min-h-12 items-center gap-2 bg-[#102441] px-4 text-white">
          <h2 className="min-w-0 flex-1 truncate text-sm font-bold">{title}</h2>
          <button type="button" aria-label="Minimieren" onClick={close} className="rounded p-1.5 hover:bg-white/10"><Minus className="h-4 w-4" /></button>
          <button type="button" aria-label={expanded ? "Verkleinern" : "Vergrößern"} onClick={() => setExpanded((value) => !value)} className="hidden rounded p-1.5 hover:bg-white/10 sm:block"><Maximize2 className="h-4 w-4" /></button>
          <button type="button" aria-label="Schließen" onClick={close} className="rounded p-1.5 hover:bg-white/10"><X className="h-4 w-4" /></button>
        </header>
        <div className="border-b border-line px-4">
          {accounts.length > 1 ? (
            <label className="flex min-h-12 items-center gap-2 border-b border-line">
              <span className="text-sm text-ink-muted">Von</span>
              <select value={from} onChange={(event) => { dirty.current = true; setFrom(event.target.value); }} aria-label="Von" className="min-w-0 flex-1 bg-transparent py-3 text-sm text-ink outline-none">
                {accounts.map((account) => <option key={account.id} value={account.id}>{account.name === account.address ? account.address : `${account.name} <${account.address}>`}{account.personal ? "" : " · geteilt"}</option>)}
              </select>
            </label>
          ) : null}
          <div className="flex min-h-12 items-center gap-2 border-b border-line"><span className="text-sm text-ink-muted">An</span><input autoFocus={!replyTo} value={fields.to} onChange={(event) => update({ to: event.target.value })} aria-label="An" placeholder="name@unternehmen.de" className="min-w-0 flex-1 bg-transparent py-3 text-sm text-ink outline-none" /><button type="button" onClick={() => setShowCc((value) => !value)} className="text-xs font-semibold text-ink-muted hover:text-ink">Cc</button></div>
          {showCc ? <div className="flex min-h-12 items-center gap-2 border-b border-line"><span className="text-sm text-ink-muted">Cc</span><input value={fields.cc} onChange={(event) => update({ cc: event.target.value })} aria-label="Cc" className="min-w-0 flex-1 bg-transparent py-3 text-sm text-ink outline-none" /></div> : null}
          <input value={fields.subject} onChange={(event) => update({ subject: event.target.value })} aria-label="Betreff" placeholder="Betreff" className="min-h-12 w-full bg-transparent py-3 text-sm font-semibold text-ink outline-none" />
        </div>
        <textarea autoFocus={Boolean(replyTo)} value={fields.body} onChange={(event) => update({ body: event.target.value })} aria-label="Nachricht" placeholder="Nachricht schreiben …" className="min-h-52 flex-1 resize-none bg-surface-raised p-4 text-[15px] leading-6 text-ink outline-none" />
        <footer className="flex items-center gap-2 border-t border-line px-4 pb-[calc(theme(spacing.3)+env(safe-area-inset-bottom))] pt-3 sm:pb-3">
          <PrimaryButton type="submit" disabled={!fields.to.trim() || send.isPending} className="inline-flex items-center gap-2 px-5"><Send className="h-4 w-4" />Senden</PrimaryButton>
          <span className={send.error ? "min-w-0 truncate text-xs text-danger" : "min-w-0 truncate text-xs text-ink-muted"}>{status}</span>
          <button type="button" onClick={discard} aria-label="Entwurf verwerfen" className="ml-auto grid h-10 w-10 shrink-0 place-items-center rounded-lg text-ink-muted hover:bg-danger/10 hover:text-danger"><Trash2 className="h-4 w-4" /></button>
        </footer>
      </form>
    </div>
  );
}

function format(address: { name?: string | null; email: string }): string {
  return address.name ? `${address.name.includes(",") ? `"${address.name}"` : address.name} <${address.email}>` : address.email;
}

export function replySubject(subject: string): string {
  return /^(re|aw):/i.test(subject.trim()) ? subject.trim() : `Re: ${subject.trim()}`;
}

function quote(message: MailMessageDTO): string {
  const when = new Date(message.receivedAt).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
  const who = message.from ? format(message.from) : "Unbekannt";
  return `\n\nAm ${when} schrieb ${who}:\n${message.text.split("\n").map((line) => `> ${line}`).join("\n")}`;
}
